import { PostOrderField } from '__generated__/graphql/legacy/graphql';
import classNames from 'classnames/bind';
import { features } from 'constants/featureFlags';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { useCurrentUser } from 'hooks/new/useCurrentUser';
import { PageInfo } from 'interfaces/pageInfo';
import Button from 'koba/components/Button';
import { SkeletonPostContainer } from 'koba/components/SkeletonPost/SkeletonPost';
import { TFunction } from 'next-i18next';
import { useRouter } from 'next/router';
import { Post } from 'types/post';
import {
  NUMBER_OF_REACTIONS,
  POSTS_PER_PAGE,
  POSTS_PER_PAGE_ON_HOME,
  POST_ORDER_FIELDS,
  SORT_BY_PARAMS,
  SORT_BY_TO_POST_ORDER_FIELDS,
  TRENDING_POSTS_MAX,
} from 'utils/constants';
import { replaceMentions } from 'utils/mentions';
import { pathnames } from 'utils/routerHelpers';
import { genericToastAlert } from 'utils/toastHelpers';
import { canPostInReadOnlySpace } from 'utils/userHelpers';

import React, { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';

import { useCommunityContext } from 'components/CommunityContext';
import PostContainer from 'components/PostContainer/PostContainer';
import PostListSort from 'components/PostListSort';
import { HorizontalDivider } from 'components/new/Posts/HorizontalDivider/HorizontalDivider';

import { POST_LIST_QUERY } from 'schema/Post/queries';
import { UPDATE_COMMUNITY_SPACE_LAST_READ_MUTATION } from 'schema/User/mutations';

import styles from './PostList.module.scss';

const cx = classNames.bind(styles);

interface PostListProps {
  currentSpaceId?: string;
  customEmptyState?: ReactNode;
  emptyTitle: string;
  emptyMessage?: string;
  filterByFollowing?: boolean;
  isTrending?: boolean;
  t: TFunction;
  showSpace?: boolean;
  sharedState: {
    useCacheFirst: boolean;
  };
}

export const PostList: React.FC<PostListProps> = ({
  t,
  customEmptyState,
  showSpace,
  emptyTitle,
  emptyMessage,
  isTrending = false,
  filterByFollowing = false,
  sharedState,
  currentSpaceId,
}) => {
  const [showDivider, setShowDivider] = useState(false);
  const [hasOnlyPinnedUnreadPost, setHasOnlyPinnedUnreadPost] = useState(false);
  const [divider, setDivider] = useState<HTMLDivElement | null>(null);
  const [lastReadPostId, setLastReadPostId] = useState<string | null>('');
  const hasMarkedAsRead = useRef(false);

  const fetchPolicy = sharedState.useCacheFirst ? 'cache-first' : 'cache-and-network';
  if (sharedState.useCacheFirst) {
    // If another page has set the useCacheFirst flag, that means it wants to preserve the current Post List.
    // This is useful if a user has clicked Load More, clicked on a post, and wants to return to the same scroll
    // location. Is it hacky? Yes. Can we do better? One day I hope.
    // eslint-disable-next-line no-param-reassign
    sharedState.useCacheFirst = false;
  }

  const router = useRouter();

  const { community, trackEvent, featureFlags, currentUser } = useCommunityContext();
  const multipleReactionsEnabled = featureFlags[features.COMMUNITY_REACTIONS] || false;

  const {
    updateSpaceInteraction,
    getSpaceInteraction,
    loading: currentUserLoading,
  } = useCurrentUser(community.id);

  const { spaceId, sortBy } = router.query;
  const sortByKey = Array.isArray(sortBy) ? sortBy[0] : sortBy;

  const isHomePage = showSpace && !filterByFollowing;

  let postOrderField = isHomePage
    ? PostOrderField.CreatedAt
    : SORT_BY_TO_POST_ORDER_FIELDS[sortByKey || SORT_BY_PARAMS.RECENT];

  let numOfPostsPerPage = isHomePage ? POSTS_PER_PAGE_ON_HOME : POSTS_PER_PAGE;

  // trending posts
  postOrderField = isTrending ? PostOrderField.TrendingScore : postOrderField;
  numOfPostsPerPage = isTrending ? TRENDING_POSTS_MAX : numOfPostsPerPage;

  const defaultEmptyState = (
    <div
      className={cx('spaces-enabled-empty-state', {
        'is-home': router.pathname === pathnames.home,
      })}
    >
      <span>{emptyTitle}</span>
      <p>{emptyMessage}</p>
    </div>
  );

  const { loading, data, refetch, previousData, fetchMore } = useQuery(POST_LIST_QUERY, {
    fetchPolicy,
    // When this prop is specied, fetchPolicy is used for the query's first execution,
    // and nextFetchPolicy is used to determine how the query responds to future cache updates.
    // Here is a more detailed explanation of why do we need this from now on
    // https://github.com/apollographql/apollo-client/issues/6760#issuecomment-668188727
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      communityId: community.id,
      numOfPosts: numOfPostsPerPage,
      postOrderField,
      filter: {
        following: filterByFollowing,
        communitySpaceId: spaceId,
        trending: isTrending,
      },
      numberOfReactions: NUMBER_OF_REACTIONS,
      multipleReactionsEnabled,
    },
  });

  const postEdges = data?.community?.posts?.edges || previousData?.community?.posts?.edges || [];
  const posts: Post[] = postEdges.map((edge) => edge?.node as Post);

  const { pinnedPosts, regularPosts } = useMemo(() => {
    const pinned: Post[] = [];
    const regular: Post[] = [];
    posts.forEach((post) => (post.pinnedAt ? pinned.push(post) : regular.push(post)));
    return { pinnedPosts: pinned, regularPosts: regular };
  }, [posts]);

  const pageInfo: PageInfo =
    data?.community?.posts?.pageInfo || previousData?.community?.posts?.pageInfo;

  const onLoadMorePosts = async () => {
    await fetchMore({ variables: { endCursor: pageInfo.endCursor } });
  };

  const [updateCommunitySpaceLastReadMutation] = useMutation(
    UPDATE_COMMUNITY_SPACE_LAST_READ_MUTATION,
    {
      onError: genericToastAlert,
      update: (cache, { data: { updateCommunitySpaceLastRead } }) => {
        updateSpaceInteraction({
          cache,
          spaceId: currentSpaceId as string,
          hasUnreadPosts: false,
          lastReadAt: updateCommunitySpaceLastRead.lastReadAt,
        });
      },
    }
  );

  useEffect(() => {
    if (currentUserLoading || hasMarkedAsRead.current || !currentSpaceId) return;
    const interaction = getSpaceInteraction(currentSpaceId as string);

    if (
      interaction.hasUnreadPosts &&
      postOrderField === POST_ORDER_FIELDS.PINNED_AT &&
      interaction.lastReadAt
    ) {
      setShowDivider(true);
      setHasOnlyPinnedUnreadPost(
        pinnedPosts.length > 0 &&
          (regularPosts.length === 0 ||
            new Date(regularPosts[0]?.createdAt).getTime() <=
              new Date(interaction.lastReadAt).getTime())
      );
      setLastReadPostId(
        regularPosts.find((post) => {
          return new Date(post.createdAt).getTime() <= new Date(interaction.lastReadAt).getTime();
        })?.id ?? null
      );
    } else {
      setShowDivider(false);
    }
  }, [currentUserLoading, currentSpaceId, regularPosts, pinnedPosts]);

  useLayoutEffect(() => {
    if (!divider || !showDivider || hasMarkedAsRead.current) return;
    const updateUnreadPosts = () => {
      updateCommunitySpaceLastReadMutation({
        variables: {
          input: {
            id: posts[0]?.communitySpace?.id,
          },
        },
      });
      hasMarkedAsRead.current = true;
      trackEvent(TrackingEvent.UNREAD_POSTS_READ, {
        [TrackingProperty.SPACE_ID]: posts[0]?.communitySpace?.id,
      });
    };

    const rect = divider.getBoundingClientRect();
    const isVisible =
      rect.top < window.innerHeight &&
      rect.bottom > 0 &&
      rect.left < window.innerWidth &&
      rect.right > 0;
    if (isVisible) {
      updateUnreadPosts();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            updateUnreadPosts();
            observer.disconnect();
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
        rootMargin: '0px',
      }
    );

    observer.observe(divider);
  }, [updateCommunitySpaceLastReadMutation, lastReadPostId, showDivider, divider]);

  if (!data && !previousData && loading) {
    // If we haven't completed our initial load, show the skeleton
    return <SkeletonPostContainer />;
  }

  if (posts.length === 0) {
    if (!canPostInReadOnlySpace(currentUser)) {
      return <></>;
    }
    return customEmptyState ? <>{customEmptyState}</> : <>{defaultEmptyState}</>;
  }

  return (
    <>
      {(!showSpace || filterByFollowing) && <PostListSort />}
      {pinnedPosts.map((post) => {
        const content = replaceMentions(post?.content, post?.mentionedUsers);
        return (
          <PostContainer
            author={post.user}
            content={content}
            key={post.id}
            mentionedUsers={post?.mentionedUsers}
            post={post}
            refetchPostList={refetch}
            showSpace={showSpace}
            t={t}
          />
        );
      })}
      {showDivider &&
        (hasOnlyPinnedUnreadPost ? (
          <HorizontalDivider
            ref={setDivider}
            text={t('components-postList-you_are_all_caught_up', 'You are all caught up')}
            hidden
          />
        ) : (
          <HorizontalDivider text={t('components-postList-new_posts', 'New posts')} />
        ))}

      {regularPosts.map((post) => {
        const content = replaceMentions(post.content, post.mentionedUsers);
        return !hasOnlyPinnedUnreadPost && post.id === lastReadPostId && showDivider ? (
          <div key={post.id}>
            <HorizontalDivider
              ref={setDivider}
              text={t('components-postList-you_are_all_caught_up', 'You are all caught up')}
            />
            <PostContainer
              author={post.user}
              content={content}
              disableReplyAction={isHomePage}
              mentionedUsers={post?.mentionedUsers}
              post={post}
              refetchPostList={refetch}
              showSpace={showSpace}
              t={t}
            />
          </div>
        ) : (
          <PostContainer
            author={post.user}
            content={content}
            disableReplyAction={isHomePage}
            key={post.id}
            mentionedUsers={post?.mentionedUsers}
            post={post}
            refetchPostList={refetch}
            showSpace={showSpace}
            t={t}
          />
        );
      })}
      {!hasOnlyPinnedUnreadPost && !pageInfo?.hasNextPage && !lastReadPostId && showDivider && (
        <HorizontalDivider
          ref={setDivider}
          text={t('components-postList-you_are_all_caught_up', 'You are all caught up')}
        />
      )}
      {pageInfo?.hasNextPage && !isTrending && (
        <Button
          appearance="ghost"
          className={cx('load-more')}
          data-qa={cx('load-more')}
          onClick={onLoadMorePosts}
        >
          {showSpace ? t('See More') : t('Load More')}
        </Button>
      )}
    </>
  );
};

export default PostList;
